<template>
  <div class="user-settings">
    <div class="box">
      <h3>Account status</h3>
      <div class="setting-item">
        <base-stack class="setting-info">
          <strong>Block user</strong>
          <p>
            Block this user from logging in and using the platform. They will see an "email not verified" message when
            trying to login.
          </p>
          <p v-if="user.blockedAt" class="blocked-info">
            User has been blocked since {{ user.blockedAt | dayjs('MMM DD, YYYY HH:mm') }}
          </p>
        </base-stack>
        <div class="setting-actions">
          <base-button v-if="!user.blockedAt" @click="handleBlockUser" :loading="loading" variant="danger">
            Block user
          </base-button>
          <base-button v-else @click="handleUnblockUser" :loading="loading" variant="success">
            Unblock user
          </base-button>
        </div>
      </div>
    </div>

    <div class="box">
      <h3>Sessions</h3>
      <div class="setting-item">
        <base-stack class="setting-info">
          <strong>Active sessions</strong>
          <p>
            User currently has <strong>{{ user.activeSessionCount }}</strong> active
            {{ user.activeSessionCount === 1 ? 'session' : 'sessions' }}.
          </p>
        </base-stack>
        <div class="setting-actions">
          <base-button
            @click="handleClearSessions"
            :loading="sessionsLoading"
            :disabled="user.activeSessionCount === 0"
            variant="danger"
          >
            Clear sessions
          </base-button>
        </div>
      </div>
    </div>

    <div v-if="user.citizenshipSubscription" class="box">
      <h3>Citizenship Subscription</h3>
      <div class="setting-item">
        <base-stack class="setting-info">
          <base-stack as="dl" class="setting-info">
            <base-stack direction="row" justify="space-between">
              <dt>Subscription Status:</dt>
              <dd>{{ formatSubscriptionStatus(user.citizenshipSubscription.status) }}</dd>
            </base-stack>
            <base-stack direction="row" justify="space-between">
              <dt>Plan:</dt>
              <dd>
                {{ formatPrice(user.citizenshipSubscription.price) }} / {{ user.citizenshipSubscription.interval }}
              </dd>
            </base-stack>
            <base-stack direction="row" justify="space-between">
              <dt>Current Period Ends:</dt>
              <dd>{{ user.citizenshipSubscription.currentPeriodEnd | dayjs('MMM DD, YYYY HH:mm') }}</dd>
            </base-stack>
            <base-stack direction="row" justify="space-between" v-if="user.citizenshipSubscription.willCancelAt">
              <dt>Cancel At:</dt>
              <dd>{{ user.citizenshipSubscription.willCancelAt | dayjs('MMM DD, YYYY HH:mm') }}</dd>
            </base-stack>
          </base-stack>
          <p v-if="!user.citizenshipSubscription.willCancelAt">
            Cancel this user's citizenship subscription. This will stop future billing and their citizenship will expire
            at the end of the current billing period.
          </p>
        </base-stack>
        <div class="setting-actions">
          <base-button
            v-if="!user.citizenshipSubscription.willCancelAt"
            @click="handleCancelSubscription"
            :loading="subscriptionLoading"
            variant="danger"
          >
            Cancel Subscription
          </base-button>
          <base-button v-else :disabled="true" variant="danger">Subscription Cancelled</base-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { User } from '@/types/api';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import { Component, Prop, Vue } from 'vue-property-decorator';

@Component
export default class UserSettings extends Vue {
  @Prop({ required: true }) user!: User;

  loading = false;
  sessionsLoading = false;
  subscriptionLoading = false;

  get isBlocked(): boolean {
    return !!this.user.blockedAt;
  }

  async handleClearSessions() {
    const confirmed = await this.$modal.confirm({
      title: 'Clear sessions',
      message: `Are you sure you want to clear all ${this.user.activeSessionCount} active session(s) for ${
        this.user.username || this.user.email
      }? This will log them out from all devices.`,
      confirmButtonText: 'Yes, clear sessions',
      cancelButtonText: 'Cancel',
    });

    if (!confirmed) return;

    try {
      this.sessionsLoading = true;
      await this.$apollo.mutate({
        mutation: gql`
          mutation ClearUserSessions($id: ID!) {
            clearUserSessions(id: $id) {
              user {
                id
                activeSessionCount
              }
            }
          }
        `,
        variables: {
          id: this.user.id,
        },
        refetchQueries: ['User'],
        awaitRefetchQueries: true,
      });

      this.$modal.alert({
        title: 'Sessions cleared',
        message: `All sessions for ${this.user.username || this.user.email} have been cleared.`,
      });
    } catch (error: any) {
      this.$modal.alert({
        title: 'Error',
        message: error.message || 'Failed to clear sessions. Please try again.',
      });
    } finally {
      this.sessionsLoading = false;
    }
  }

  async handleBlockUser() {
    const confirmed = await this.$modal.confirm({
      title: 'Block user',
      message: `Are you sure you want to block ${this.user.username || this.user.email}? This will:
      
• Immediately log them out from all devices
• Prevent them from logging in (they'll see "email not verified")
• Block all platform functionality
• Not send them any verification emails
• Cancel any citizenship subscription immediately

This action can be reversed by unblocking the user.`,
      confirmButtonText: 'Yes, block user',
      cancelButtonText: 'Cancel',
    });

    if (!confirmed) return;

    try {
      this.loading = true;
      await this.$apollo.mutate({
        mutation: gql`
          mutation BlockUser($id: ID!) {
            blockUser(id: $id) {
              user {
                id
                blockedAt
              }
            }
          }
        `,
        variables: {
          id: this.user.id,
        },
        refetchQueries: ['User'],
        awaitRefetchQueries: true,
      });

      this.$modal.alert({
        title: 'User Blocked',
        message: `${this.user.username || this.user.email} has been successfully blocked.`,
      });
    } catch (error: any) {
      this.$modal.alert({
        title: 'Error',
        message: error.message || 'Failed to block user. Please try again.',
      });
    } finally {
      this.loading = false;
    }
  }

  async handleUnblockUser() {
    const confirmed = await this.$modal.confirm({
      title: 'Unblock user',
      message: `Are you sure you want to unblock ${
        this.user.username || this.user.email
      }? This will restore their access to the platform.`,
      confirmButtonText: 'Yes, unblock user',
      cancelButtonText: 'Cancel',
    });

    if (!confirmed) return;

    try {
      this.loading = true;
      await this.$apollo.mutate({
        mutation: gql`
          mutation UnblockUser($id: ID!) {
            unblockUser(id: $id) {
              user {
                id
                blockedAt
              }
            }
          }
        `,
        variables: {
          id: this.user.id,
        },
        refetchQueries: ['User'],
        awaitRefetchQueries: true,
      });

      this.$modal.alert({
        title: 'User unblocked',
        message: `${this.user.username || this.user.email} has been successfully unblocked.`,
      });
    } catch (error: any) {
      this.$modal.alert({
        title: 'Error',
        message: error.message || 'Failed to unblock user. Please try again.',
      });
    } finally {
      this.loading = false;
    }
  }

  formatSubscriptionStatus(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'Active';
      case 'INCOMPLETE':
        return 'Incomplete';
      case 'REQUIRES_ACTION':
        return 'Requires Action';
      case 'TRIAL':
        return 'Trial';
      default:
        return status;
    }
  }

  formatPrice(priceInCents: number): string {
    return `$${(priceInCents / 100).toFixed(2)}`;
  }

  async handleCancelSubscription() {
    const confirmed = await this.$modal.confirm({
      title: 'Cancel citizenship subscription',
      message: `Are you sure you want to cancel ${this.user.username || this.user.email}'s citizenship subscription?

• This will stop future billing immediately
• Their citizenship will remain active until the end of the current billing period
• They will lose access to citizen-only features after the current period ends
• This action cannot be easily undone

Current period ends: ${dayjs(this.user.citizenshipSubscription?.currentPeriodEnd).format('MMM DD, YYYY HH:mm')}`,
      confirmButtonText: 'Yes, cancel subscription',
      cancelButtonText: 'Keep subscription',
    });

    if (!confirmed) return;

    try {
      this.subscriptionLoading = true;
      await this.$apollo.mutate({
        mutation: gql`
          mutation CancelCitizenshipSubscription($input: CancelCitizenshipSubscriptionInput!) {
            cancelCitizenship(input: $input) {
              user {
                id
                citizenshipSubscription {
                  status
                  price
                  interval
                  currentPeriodEnd
                  willCancelAt
                }
              }
            }
          }
        `,
        variables: {
          input: {
            userId: this.user.id,
            immediate: false,
          },
        },
        refetchQueries: ['User'],
        awaitRefetchQueries: true,
      });

      this.$modal.alert({
        title: 'Subscription cancelled',
        message: `${
          this.user.username || this.user.email
        }'s citizenship subscription has been successfully cancelled. They will retain access until ${dayjs(
          this.user.citizenshipSubscription?.currentPeriodEnd
        ).format('MMM DD, YYYY HH:mm')}.`,
      });
    } catch (error: any) {
      this.$modal.alert({
        title: 'Error',
        message: error.message || 'Failed to cancel subscription. Please try again.',
      });
    } finally {
      this.subscriptionLoading = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.user-settings {
  padding: 1rem 0;
}

.box {
  margin: 16px 0;
  padding: 16px;
  border: 1px solid $color-theme-red;

  h3 {
    font-weight: bold;
    margin-bottom: 16px;
  }
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;

  .setting-info {
    flex: 1;

    dl {
      max-width: 400px;
    }

    dt {
      color: $color-theme-red;
      font-weight: bold;
    }

    p {
      color: #666;
    }

    .blocked-info {
      color: $color-theme-red;
      font-weight: 500;
    }

    .cancel-info {
      color: $color-theme-yellow;
      font-weight: 500;
    }
  }

  .setting-actions {
    flex-shrink: 0;
  }
}

@media (max-width: 768px) {
  .setting-item {
    flex-direction: column;
    align-items: stretch;

    .setting-actions {
      margin-top: 16px;
    }
  }
}
</style>
